import { useQuery } from '@apollo/client';

import LEADS_ACCESS from 'app/features/leads/graphql/leads-access.gql';
import type { LeadsAccessQuery, LeadsAccessQueryVariables } from 'app/types/graphql';
import { canPerform, type PermissionAction } from 'app/features/leads/utils/can-perform-partner-permission';
import { useAppState } from 'state';

// Answers "may the signed-in user perform <action> on <resource> in the current partner?" from
// User.partnerPermissions — the same projection (and the same cached query) the Leads gate reads.
export const usePartnerPermission = (resource: string, action: PermissionAction) => {
  const userId = useAppState(({ auth }) => auth.userId);
  const hasUser = userId != null;

  const { data, loading } = useQuery<LeadsAccessQuery, LeadsAccessQueryVariables>(LEADS_ACCESS, {
    variables: hasUser ? { id: userId } : undefined,
    skip: !hasUser,
    fetchPolicy: 'cache-first',
  });

  const permissions = data?.userById?.partnerPermissions ?? [];
  const currentPartnerId = data?.myPartners.currentPartnerId ?? null;

  return {
    isAllowed: hasUser && !loading && canPerform(permissions, resource, action, currentPartnerId),
    isLoading: hasUser && loading,
  };
};

// workout-builder's duplicateLesson asserts Lesson:update, so that is what decides whether the
// "Clone workout" button (and the Workout Builder tile) is offered at all (JS-9612).
export const useLessonCloneAccess = () => {
  const { isAllowed, isLoading } = usePartnerPermission('Lesson', 'update');

  return { canCloneWorkout: isAllowed, isLoading };
};
